import { OmitStrict } from "@amx/ux-twoui-utils";
import { Stack } from "@fluentui/react";
import React, { useEffect } from "react";
import { BaseProps, validateProps } from "../../../shared/props";
import { warnDeprecation } from "../../../shared/warnDeprecation";
import { IconSize, useViewport } from "../../../styles";
import { HeadingTypes } from "../../../styles/fonts";
import { Icon, IconProps } from "../../Icon/Icon";
import { Image } from "../../Image/Image";
import { AltImageProps } from "../../Image/Image.types";
import { TruncatedText } from "../../TruncatedText/TruncatedText";
import { getBannerTitleColumnStyles, getBannerTitleIconStyles, getBannerTitleImageStyles } from "./BannerTitleColumn.styles";

export const bannerTitleTag = "h1";
export interface BannerTitleColumnAltImageProps extends OmitStrict<AltImageProps, "height" | "width"> {
    readonly kind: "image";
}

export interface BannerTitleColumnIconProps extends OmitStrict<IconProps, "size"> {
    readonly kind: "icon";
}

export interface BannerTitleColumnProps extends BaseProps {
    /**
     * bannerTitle is a string that represents the title in the banner
     */
    readonly bannerTitle: string;
    /**
     * mediaProps allows you to include an optional image or icon beside the title
     * (for instance a logo). Only works well for square images/icons
     */
    readonly mediaProps?: BannerTitleColumnAltImageProps | BannerTitleColumnIconProps;
}

/** @deprecated Please use BannerBreadcrumbTitleColumn instead. */
export const BannerTitleColumn: React.FC<BannerTitleColumnProps> = ({
    bannerTitle,
    mediaProps,
    ...otherProps
}) => {
    validateProps("BannerTitleColumn", otherProps);
    const viewportType = useViewport();

    useEffect(() => {
        warnDeprecation("BannerPersonaColumn has been deprecated, please use the new BannerBreadcrumbTitleColumn instead.");
    }, []);

    let headingVariant: HeadingTypes = "pageTitle";
    if (viewportType === "s") {
        headingVariant = "paneTitle";
    } else if (["m", "l"].includes(viewportType)) {
        headingVariant = "statusHeader";
    }

    let BannerIconSize: IconSize = "32px";
    if (viewportType === "s") {
        BannerIconSize = "16px";
    } else if (["m", "l"].includes(viewportType)) {
        BannerIconSize = "24px";
    }

    let mediaNode: JSX.Element | undefined;
    switch (mediaProps?.kind) {
        case "image":
            const smallImageSize = 36;
            const largeImageSize = 38;
            const imageSize = viewportType === "s" ? smallImageSize : largeImageSize;
            mediaNode = (
                <Stack styles={getBannerTitleImageStyles()} verticalAlign="center">
                    <Image
                        imageType="altTextImage"
                        {...(mediaProps as AltImageProps)}
                        height={imageSize}
                        width={imageSize}
                    />
                </Stack>
            );
            break;
        case "icon":
            mediaNode = (
                <Stack styles={getBannerTitleIconStyles()} verticalAlign="center">
                    <Icon
                        {...(mediaProps as IconProps)}
                        size={BannerIconSize}
                    />
                </Stack>
            );
            break;
    }

    return (
        <Stack horizontal={true} styles={getBannerTitleColumnStyles()}>
            {mediaNode}
            <Stack verticalAlign="center">
                <TruncatedText
                    type="heading"
                    tag={bannerTitleTag}
                    lines={1}
                    variant={headingVariant}
                    {...otherProps}
                >
                    {bannerTitle}
                </TruncatedText>
            </Stack>
        </Stack>
    );
};
